import { Button, DetailList, RecordDrawer, TextButton } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { FileDropzone } from "@majitap/ui/arc/file-dropzone/file-dropzone";
import { Input } from "@majitap/ui/arc/input/input";
import { RadioCards } from "@majitap/ui/arc/radio-cards/radio-cards";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Select } from "@majitap/ui/arc/select/select";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { refreshCounts } from "../../counts";
import { isFileKind, type ProofItem, proofChecklistQuery } from "../../deliveries";
import {
  type BoardDelivery,
  type CompleteForDriver,
  dispatchActions,
  fleetQueries,
  RESUME_TO,
  type ResumeTo,
  STAGE_ACTION,
  type Stage,
} from "../../dispatch";
import { messageFor } from "../../error-copy";
import { uploadProofFile } from "../../files";
import { DELIVERY_STATUS } from "../../labels";
import type { ReasonScope } from "../../reasons";
import { kmBetween, truckPositionsQuery, whereabouts } from "../../tracking";
import { ReasonFields, useReasonPicker } from "../shared/reason-fields";
import styles from "./dispatch.module.css";

/** Proof photos, as the API takes them. */
const MAX_PHOTO = 10 * 1024 * 1024;

const placeOf = (d: BoardDelivery) =>
  [d.location?.label, d.location?.floor ? `floor ${d.location.floor}` : null].filter(Boolean).join(", ");

/** Changes to a delivery show on the board at once: refetch it (and the order's page). */
export function useRefreshBoard() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["dispatch-board"] });
    // Dispatch's table and its counts (P8-76)
    await queryClient.invalidateQueries({ queryKey: ["dispatch-table"] });
    await queryClient.invalidateQueries({ queryKey: ["order"] });
    // a delivery's own page shows the same delivery (P8-48)
    await queryClient.invalidateQueries({ queryKey: ["delivery"] });
    await refreshCounts(queryClient);
  };
}

/**
 * Plan a delivery (`Console / Dispatch / Assign` in the design): its fill site, then a driver on
 * shift. The truck and assistant are the ones the driver started the shift with (P1-19). Each step
 * is its own call, and the board row follows. `onPostpay`, for staff who may, offers to let an unpaid
 * order go out (P8-39); the board shows that dialog in place of this drawer, as a dialog opened
 * inside the drawer would dismiss it.
 */
export function AssignDrawer({
  delivery,
  onClose,
  onPostpay,
}: {
  delivery: BoardDelivery;
  onClose: () => void;
  onPostpay?: () => void;
}) {
  const refresh = useRefreshBoard();
  const fillsites = useQuery(fleetQueries.fillsites);
  const shifts = useQuery(fleetQueries.shifts);
  const current = delivery.assignment;
  const [changingFillsite, setChangingFillsite] = useState(!current?.fillsite);
  const [fillsiteId, setFillsiteId] = useState(current?.fillsite?.id ?? "");
  const [shiftId, setShiftId] = useState<string | null>(null);

  // why a fill site or a driver changed goes on the delivery's timeline
  const [reason, setReason] = useState("");
  const setFillsite = useMutation({
    mutationFn: () =>
      dispatchActions.fillsite(delivery.id, fillsiteId, current?.fillsite ? reason.trim() || undefined : undefined),
    onSuccess: async () => {
      await refresh();
      setChangingFillsite(false);
    },
  });
  const positions = useQuery(truckPositionsQuery);
  // nearest first to the fill site the truck goes to (P1-22); trucks never seen or not seen lately last
  const site = fillsites.data?.find((f) => f.id === (fillsiteId || current?.fillsite?.id));
  const positionOf = (truckId: string | undefined) => positions.data?.data.find((p) => p.truck_id === truckId);
  const kmFor = (truckId: string | undefined) => {
    const p = positionOf(truckId);
    return p && !p.stale ? kmBetween(p, site) : null;
  };
  // a shift without a truck (from before the checklist) can't take a delivery
  const onShift = (shifts.data ?? [])
    .filter((s) => s.truck)
    .map((s) => ({ ...s, km: kmFor(s.truck?.id) }))
    .sort((a, b) => (a.km ?? Number.POSITIVE_INFINITY) - (b.km ?? Number.POSITIVE_INFINITY));
  const shift = onShift.find((s) => s.id === shiftId);
  const assign = useMutation({
    mutationFn: () =>
      dispatchActions.assign(delivery.id, {
        driver_id: shift?.driver.id ?? "",
        reason: current?.driver ? reason.trim() || undefined : undefined,
      }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const fillsiteName =
    fillsites.data?.find((f) => f.id === (current?.fillsite?.id ?? fillsiteId))?.name ?? current?.fillsite?.name;

  return (
    <RecordDrawer
      open
      onOpenChange={(open) => !open && onClose()}
      title={`${current?.driver ? "Reassign" : "Assign"} ${delivery.delivery_number}`}
      description={[delivery.order.order_number, delivery.order.customer_name, placeOf(delivery)]
        .filter(Boolean)
        .join(" · ")}
    >
      <section className={styles.step}>
        <h3 className={styles.stepTitle}>1. Fill site</h3>
        {current?.fillsite && !changingFillsite ? (
          <div className={styles.chosen}>
            <span>{fillsiteName}</span>
            <TextButton onClick={() => setChangingFillsite(true)}>Change</TextButton>
          </div>
        ) : (
          <div className={styles.inline}>
            <Select
              label="Fill site"
              placeholder="Choose a fill site"
              value={fillsiteId}
              onValueChange={setFillsiteId}
              options={(fillsites.data ?? []).map((f) => ({ value: f.id, label: f.name }))}
            />
            <Button
              variant="secondary"
              disabled={!fillsiteId}
              loading={setFillsite.isPending}
              onClick={() => setFillsite.mutate()}
            >
              Use this fill site
            </Button>
          </div>
        )}
        {setFillsite.error ? (
          <Alert tone="danger" title="The fill site wasn't set">
            {messageFor(errorOf(setFillsite.error))}
          </Alert>
        ) : null}
      </section>

      <section className={styles.step}>
        <h3 className={styles.stepTitle}>2. Driver</h3>
        <p className={styles.hint}>
          Drivers on shift now, with the truck and assistant they started the shift with
          {site ? `, nearest to ${site.name} first` : ""}.
        </p>
        {onShift.length === 0 ? (
          <p className={styles.hint}>Nobody is on shift. Drivers start a shift in the driver app.</p>
        ) : (
          <RadioCards
            layout="list"
            aria-label="Driver on shift"
            value={shiftId}
            onValueChange={setShiftId}
            options={onShift.map((s) => ({
              value: s.id,
              label: s.driver.name,
              description: [
                s.truck?.plate_number,
                s.assistant ? `with ${s.assistant.name}` : "no assistant",
                whereabouts(positionOf(s.truck?.id), s.km, site?.name ?? null),
              ]
                .filter(Boolean)
                .join(" · "),
            }))}
          />
        )}
      </section>

      {current?.fillsite || current?.driver ? (
        <Input
          label="Reason for the change (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Truck broke down"
          description="It goes on the delivery's timeline with the new fill site or driver."
        />
      ) : null}

      {assign.error ? (
        <Alert tone="danger" title="Not assigned">
          {messageFor(errorOf(assign.error))}
          {/* a prepaid order (or a payer over their limit): finance can let it go out now (P8-39) */}
          {onPostpay && ["PAYMENT_REQUIRED", "CREDIT_LIMIT_EXCEEDED"].includes(errorOf(assign.error).code) ? (
            <div>
              <TextButton onClick={onPostpay}>
                {errorOf(assign.error).code === "CREDIT_LIMIT_EXCEEDED"
                  ? "Deliver anyway, over the credit limit"
                  : "Deliver before payment"}
              </TextButton>
            </div>
          ) : null}
        </Alert>
      ) : null}
      <div className={styles.footer}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        {/* the fill site first: assigning while it saves would plan against the old one */}
        <Button disabled={!shift || setFillsite.isPending} loading={assign.isPending} onClick={() => assign.mutate()}>
          {current?.driver ? "Reassign" : "Assign"}
        </Button>
      </div>
    </RecordDrawer>
  );
}

/**
 * Unassign, suspend or resume: each needs a reason, which goes on the delivery's timeline. With a
 * `scope`, staff pick one of the company's reasons for that status too (P15-14).
 */
export function ReasonDialog({
  title,
  description,
  action,
  danger = false,
  scope,
  run,
  onClose,
  children,
}: {
  title: string;
  description: string;
  action: string;
  danger?: boolean;
  scope?: ReasonScope;
  run: (reason: string, reasonId?: string) => Promise<unknown>;
  onClose: () => void;
  /** Choices the action takes besides the reason, shown above it. */
  children?: ReactNode;
}) {
  const refresh = useRefreshBoard();
  const picker = useReasonPicker(scope);
  const [tried, setTried] = useState(false);
  const mutation = useMutation({
    mutationFn: () => run(picker.text, picker.reasonId),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    if (!picker.missing) mutation.mutate();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={title} description={description}>
        <form onSubmit={submit} className={styles.form}>
          {children}
          {mutation.error ? (
            <Alert tone="danger" title={`${action} didn't work`}>
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <ReasonFields picker={picker} showMissing={tried} />
          <div className={styles.footer}>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant={danger ? "danger" : "primary"} loading={mutation.isPending}>
              {action}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Put a delivery on the partner job board: the first partner to claim it plans it. */
/**
 * Force reset (P8-64): back to pending, whatever the delivery's status, with the fill site, truck
 * and driver released. A delivered one's charges are reversed too, which needs ledger.adjust.
 */
export function ResetDialog({ delivery: d, onClose }: { delivery: BoardDelivery; onClose: () => void }) {
  const description =
    d.status === "delivered"
      ? "It goes back to To plan, as if new. Its charges are reversed (the customer's bill and the partner's pay) and its proof kept as history; it is charged again when it is delivered again. Needs the ledger.adjust permission, and is refused while it is on a period invoice or after the partner has asked for the pay."
      : "It goes back to To plan, as if new: the fill site, truck and driver come off it, and the driver's app drops the job.";
  return (
    <ReasonDialog
      title={`Reset ${d.delivery_number}?`}
      description={description}
      action="Reset"
      danger
      run={(reason) => dispatchActions.reset(d.id, reason)}
      onClose={onClose}
    />
  );
}

export function OfferDialog({ delivery, onClose }: { delivery: BoardDelivery; onClose: () => void }) {
  const refresh = useRefreshBoard();
  const mutation = useMutation({
    mutationFn: () => dispatchActions.offer(delivery.id),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={`Offer ${delivery.delivery_number} to partners?`}
        description="Partners with an approved fill site within 25 km see it. The first to claim it assigns their own truck and driver."
      >
        <div className={styles.form}>
          <DetailList
            items={[
              { label: "Order", value: delivery.order.order_number },
              { label: "Customer", value: delivery.order.customer_name },
              { label: "Place", value: placeOf(delivery) },
            ]}
          />
          {mutation.error ? (
            <Alert tone="danger" title="Not offered">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button loading={mutation.isPending} onClick={() => mutation.mutate()}>
              Offer to partners
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Resume a suspended delivery: where it was, back to planning, or closed as failed or cancelled. */
export function ResumeDialog({
  delivery,
  onClose,
  initial = "previous",
}: {
  delivery: BoardDelivery;
  onClose: () => void;
  /** the choice it opens on, e.g. Back to planning from the board's What next (P8-53) */
  initial?: ResumeTo;
}) {
  const [to, setTo] = useState<ResumeTo>(initial);
  return (
    <ReasonDialog
      title={`Resume ${delivery.delivery_number}?`}
      description="Say where it goes next. The reason goes on its timeline."
      action={to === "failed" || to === "cancelled" ? RESUME_TO[to].label : "Resume"}
      danger={to === "failed" || to === "cancelled"}
      // closing it as cancelled asks for a reason for cancelling a delivery
      scope={to === "cancelled" ? "delivery_cancelled" : undefined}
      run={(reason, reasonId) => dispatchActions.resume(delivery.id, reason, to, reasonId)}
      onClose={onClose}
    >
      <RadioCards
        layout="list"
        aria-label="Where it goes"
        value={to}
        onValueChange={(v) => setTo(v as ResumeTo)}
        options={(Object.keys(RESUME_TO) as ResumeTo[]).map((k) => ({
          value: k,
          label: RESUME_TO[k].label,
          description: RESUME_TO[k].help,
        }))}
      />
    </ReasonDialog>
  );
}

/** A delivery staff act on for its driver: a board row, or a delivery on an order's page. */
export interface DriverJob {
  id: string;
  delivery_number: string;
  assignment?: { driver: { name: string } | null } | null;
}

const driverOf = (d: DriverJob) => d.assignment?.driver?.name ?? "the driver";

/**
 * Move a delivery one stage on for its driver, when they can't use the app (P8-38). The timeline
 * names you, with the reason if you give one.
 */
export function ProgressDialog({ delivery, to, onClose }: { delivery: DriverJob; to: Stage; onClose: () => void }) {
  const refresh = useRefreshBoard();
  const [reason, setReason] = useState("");
  const mutation = useMutation({
    mutationFn: () => dispatchActions.progress(delivery.id, to, reason.trim() || undefined),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={`${STAGE_ACTION[to]}: ${delivery.delivery_number}?`}
        description={`For ${driverOf(delivery)}: it becomes ${DELIVERY_STATUS[to][0]}. The timeline shows you made the change.`}
      >
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate();
          }}
        >
          <Input
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Driver called in"
          />
          {mutation.error ? (
            <Alert tone="danger" title="Not updated">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {STAGE_ACTION[to]}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** One answer being filled in: a check, a reading, words, or an image to upload. */
type Draft = { bool?: boolean; text?: string; file?: File | null };

/** A yes-or-no proof answer: blank until the person picks one, like the driver app's. */
function YesNoField({
  label,
  help,
  value,
  error,
  onChange,
}: {
  label: string;
  help?: string | null;
  value: boolean | undefined;
  error?: string;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className={styles.yesNo}>
      <span className={styles.yesNoLabel} aria-hidden="true">
        {label}
      </span>
      {help ? <span className={styles.muted}>{help}</span> : null}
      <SegmentedControl
        label={label}
        value={value === undefined ? "" : value ? "yes" : "no"}
        onValueChange={(v) => onChange(v === "yes")}
        options={[
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ]}
      />
      {error ? (
        <span className={styles.fieldError} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Mark a delivery delivered for its driver (P8-38): who received it and the company's proof
 * checklist (P8-49); photos and signatures are optional for staff. The proof, the status and the
 * ledger posting go together, as in the driver app.
 */
export function CompleteDialog({ delivery, onClose }: { delivery: DriverJob; onClose: () => void }) {
  const refresh = useRefreshBoard();
  const checklist = useQuery(proofChecklistQuery);
  const [receivedBy, setReceivedBy] = useState("");
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [missing, setMissing] = useState<Record<string, string>>({});
  const items = checklist.data?.items ?? [];
  // nothing is answered for the person: a yes or no starts blank, as in the driver app
  const draftOf = (i: ProofItem): Draft => drafts[i.id] ?? {};
  const set = (id: string, d: Draft) => setDrafts((all) => ({ ...all, [id]: d }));
  const mutation = useMutation({
    mutationFn: async () => {
      // photos and signatures go up first; the answers point at them
      const answers: CompleteForDriver["answers"] = [];
      for (const i of items) {
        const d = draftOf(i);
        if (i.kind === "yes_no" && d.bool !== undefined) answers.push({ item_id: i.id, bool_value: d.bool });
        // number-ok: a reading on the proof, not money; the API takes a JSON number and keeps 2 places
        else if (i.kind === "number" && d.text?.trim()) answers.push({ item_id: i.id, number_value: Number(d.text) });
        else if (i.kind === "text" && d.text?.trim()) answers.push({ item_id: i.id, text_value: d.text.trim() });
        else if (isFileKind(i.kind) && d.file) {
          const file = await uploadProofFile(d.file, i.kind === "photo" ? "delivery_photo" : "signature");
          answers.push({ item_id: i.id, file_id: file.id });
        }
      }
      return dispatchActions.complete(delivery.id, { received_by_name: receivedBy.trim(), answers });
    },
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const problems: Record<string, string> = {};
    if (!receivedBy.trim()) problems.received_by = "Who took the delivery?";
    for (const i of items) {
      const d = draftOf(i);
      if ((i.kind === "number" || i.kind === "text") && i.required && !d.text?.trim()) problems[i.id] = "Required.";
      if (i.kind === "yes_no" && i.required && d.bool === undefined) problems[i.id] = "Answer yes or no.";
      // number-ok: only checks the reading parses, as it's sent below
      if (i.kind === "number" && d.text?.trim() && !Number.isFinite(Number(d.text))) problems[i.id] = "A number.";
    }
    setMissing(problems);
    if (!Object.keys(problems).length) mutation.mutate();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={`Mark ${delivery.delivery_number} delivered?`}
        description={`For ${driverOf(delivery)}. The customer is charged and the timeline shows you made the change.`}
      >
        <form onSubmit={submit} className={styles.form}>
          <Input
            label="Received by"
            value={receivedBy}
            onChange={(e) => setReceivedBy(e.target.value)}
            placeholder="Mary at the gate"
            error={missing.received_by}
          />
          {checklist.status === "pending" ? <p className={styles.muted}>Loading the proof checklist…</p> : null}
          {checklist.status === "error" ? (
            <Alert tone="danger" title="The proof checklist didn't load">
              {messageFor(errorOf(checklist.error))}
            </Alert>
          ) : null}
          {items.map((i) => {
            const d = draftOf(i);
            const label = i.required && !isFileKind(i.kind) ? i.label : `${i.label} (optional)`;
            if (i.kind === "yes_no")
              return (
                <YesNoField
                  key={i.id}
                  label={label}
                  help={i.help_text}
                  value={d.bool}
                  error={missing[i.id]}
                  onChange={(bool) => set(i.id, { bool })}
                />
              );
            if (i.kind === "number")
              return (
                <Input
                  key={i.id}
                  label={label}
                  inputMode="decimal"
                  description={i.help_text ?? undefined}
                  value={d.text ?? ""}
                  onChange={(e) => set(i.id, { text: e.target.value })}
                  error={missing[i.id]}
                />
              );
            if (i.kind === "text")
              return (
                <Textarea
                  key={i.id}
                  label={label}
                  rows={2}
                  description={i.help_text ?? undefined}
                  value={d.text ?? ""}
                  onChange={(e) => set(i.id, { text: e.target.value })}
                  error={missing[i.id]}
                />
              );
            return (
              <FileDropzone
                key={i.id}
                label={label}
                description="Drop a photo here or choose one"
                note="An image up to 10 MB"
                accept="image/*"
                multiple={false}
                maxFiles={1}
                compactAt={1}
                maxSize={MAX_PHOTO}
                onFilesChange={(files) => set(i.id, { file: files.find((f) => f.size <= MAX_PHOTO) ?? null })}
              />
            );
          })}
          {mutation.error ? (
            <Alert tone="danger" title="Not marked delivered">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending} disabled={checklist.status !== "success"}>
              Mark delivered
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
